import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Card } from '~/components/Global/Blocks/Card/Card'
import { MetaItem } from '~/components/Global/Blocks/Card/MetaItem'
import { NameCircle } from '~/components/Global/Elements/NameCircle/NameCircle'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useConfig } from '~/domains/configuration/queries/config'
import { participantAvatar, participantNameLink } from './TaskAside.css'

export type ParticipantQuickAction = {
  key: string
  label: string
  icon: IconDefinition
  href?: string
  /** Open `href` in a new tab (a page), instead of following it in place (a `tel:` link). */
  newTab?: boolean
  onClick?: () => void
}

type ParticipantCardProps = {
  name: string
  subtitle?: string
  pictureUrl?: string | null
  onOpen?: () => void
  actions?: ParticipantQuickAction[]
}

const resolvePictureUrl = (url: string, legacyUrl: string) =>
  url.startsWith('/') ? `${legacyUrl}${url}` : url

/** A person linked to a task, as a standard Card: avatar, name, role and quick actions. */
export const ParticipantCard = ({
  name,
  subtitle,
  pictureUrl,
  onOpen,
  actions = []
}: ParticipantCardProps): ReactElement => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')

  const quickActions = actions.map((action) => {
    const button = (
      <Button
        type="button"
        variation={ButtonVariation.Light}
        buttonSize="extraSmall"
        narrow
        onClick={action.href ? undefined : action.onClick}
      >
        <FontAwesomeIcon icon={action.icon} />
      </Button>
    )
    return (
      <Tooltip key={action.key} text={action.label} direction="top" asChild>
        {action.href ? (
          <a
            href={action.href}
            title={action.label}
            target={action.newTab ? '_blank' : undefined}
            rel={action.newTab ? 'noreferrer' : undefined}
          >
            {button}
          </a>
        ) : (
          button
        )}
      </Tooltip>
    )
  })

  return (
    <Card
      compact
      icon={
        pictureUrl ? (
          <img
            src={resolvePictureUrl(pictureUrl, legacyUrl)}
            alt={name}
            className={participantAvatar}
          />
        ) : (
          <NameCircle name={name} size={40} />
        )
      }
      title={
        <span className={clsx(onOpen && participantNameLink)} onClick={onOpen}>
          {name}
        </span>
      }
      subtitle={subtitle}
      metaData={
        quickActions.length > 0
          ? [
              <MetaItem key="actions" align="right">
                <ButtonGroup>{quickActions}</ButtonGroup>
              </MetaItem>
            ]
          : undefined
      }
    />
  )
}
